import React, { Ref, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import classnames from 'classnames';
import styles from './QuashedForm.module.scss';
import { Back, Button } from 'components/quashed';
import { IFormQuestion } from 'types';
import { getQuestionsToDisplay } from 'helpers';
import QuashedFormField from './QuashedFormField';
import { IUseForm } from 'hooks/useForm';
import { Icon } from 'semantic-ui-react';
import { ArrowRight } from 'react-feather';

interface IProps {
  className?: string;
  buttonClassName?: string;
  error?: string;
  loading?: boolean;
  errorClassName?: string;
  fieldClassName?: string;
  inputClassName?: string;
  questionsClassName?: string;
  submitText?: string;
  defaultValues?: any;
  disableSubmit?: boolean | ((v: any) => boolean);
  title?: string;
  questions: IFormQuestion[];
  getComponentProps?(question: IFormQuestion, values: any, form: IUseForm): any;
  getError?(data: any): boolean | string;
  getSubmitText?(formValues: any): string;
  onBack?(): void;
  onChange?(e: any): void;
  onSubmit(data: any): void;
  hideButtons?: boolean;
}

const QuashedForm = (
  {
    className,
    buttonClassName,
    errorClassName,
    fieldClassName,
    inputClassName,
    questionsClassName,
    submitText = 'Submit',
    defaultValues,
    disableSubmit,
    error: propsError,
    loading,
    questions,
    title,
    getError,
    getComponentProps = () => {},
    getSubmitText,
    onBack,
    onChange,
    onSubmit,
    hideButtons,
    prev,
  }: IProps,
  ref: Ref<any>,
) => {
  const form = useForm({
    defaultValues,
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = form;

  useImperativeHandle(ref, () => form);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const formValues = watch();
  const questionsToDisplay = getQuestionsToDisplay(questions, formValues);
  const error = getError ? getError(formValues) : propsError;

  return (
    <form
      className={classnames(styles.container, className)}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className={styles.contentWrap}>
        {title && <h3>{title}</h3>}
        <div className={classnames(styles.content, questionsClassName)}>
          {questionsToDisplay.map(({ children, ...question }) => {
            return (
              <QuashedFormField
                key={question.key}
                className={fieldClassName}
                control={control}
                error={errors[question.key]}
                formValues={formValues}
                inputClassName={inputClassName}
                question={question}
                onChange={onChange}
                {...getComponentProps(question, formValues, form)}
                onBack={prev}
              >
                {children}
              </QuashedFormField>
            );
          })}
        </div>
      </div>

      {!!error && (
        <div className={classnames(styles.error, errorClassName)}>
          <Icon className={styles.errorIcon} name="exclamation circle" />
          {error}
        </div>
      )}

      {!hideButtons && (
        <div className={classnames(styles.buttons, buttonClassName)}>
          <Back className={styles.back} type="button" onClick={onBack} />

          <Button
            className={styles.submit}
            type="submit"
            text={getSubmitText?.(formValues) || submitText}
            loading={loading}
            disabled={
              typeof disableSubmit === 'function'
                ? disableSubmit(formValues)
                : disableSubmit
            }
          />
        </div>
      )}
    </form>
  );
};

export default React.forwardRef(QuashedForm);
